import { valueRender } from '@repo/toolkit/conditional-render'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, LocationOneIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useFormContext } from 'react-hook-form'

import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { useNativeCountryName } from '../../../../lib/hooks/use-native-country-name'
import { OlInput, OlJourneyPlaceInput } from '../../../../lib/types/models/ol/ol-input'
import { IdInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import OlJourneyPlaceForm from './ol-journey-place-form'

const OlJourneyPlaceOlForm = () => {
  const nativeCountryName = useNativeCountryName()
  const form = useFormContext<OlInput>()
  const journeyPlaces = form.watch('journeyPlaces') || []

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    OlJourneyPlaceInput
  >(['create', 'edit', 'delete'])

  const columns: Column<IdInput<OlJourneyPlaceInput>>[] = [
    {
      enableSorting: true,
      header: 'NOM',
      accessorKey: 'name'
    },
    {
      enableSorting: true,
      header: 'Adresse',
      accessorKey: 'address1',
      cell: ({ row }) =>
        [
          row.original.address1,
          row.original.address2,
          row.original.zipCode,
          row.original.city,
          nativeCountryName(row.original.country)
        ]
          .filter(Boolean)
          .join(', ')
    },
    {
      enableSorting: true,
      header: 'Lieu des cours (si différent)',
      accessorKey: 'tuitionName',
      cell: ({ row }) => valueRender(row.original.tuitionName)
    },
    {
      enableSorting: true,
      header: 'Addresse des cours (si différent)',
      accessorKey: 'tuitionAddress1',
      cell: ({ row }) =>
        valueRender(
          [
            row.original.tuitionAddress1,
            row.original.tuitionAddress2,
            row.original.tuitionZipCode,
            row.original.tuitionCity,
            nativeCountryName(row.original.tuitionCountry)
          ]
            .filter(Boolean)
            .join(', ') || undefined
        )
    },
    {
      enableSorting: false,
      header: '',
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />

          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => toggleModal('delete', row.original)}
          />
        </div>
      )
    }
  ]

  return (
    <ActionTableWrapper
      title={'Adresses du centre'}
      actions={[
        <Button
          key={'button'}
          type="button"
          icon={PlusIcon}
          label={'Ajouter une adresse de centre'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />
      ]}
    >
      <DataTable<IdInput<OlJourneyPlaceInput>>
        manualSorting={false}
        display={journeyPlaces.length > 0}
        columns={columns}
        data={journeyPlaces.map((comment, index) => {
          return {
            ...comment,
            id: comment.id || index + (journeyPlaces.at(-1)?.id ?? 0)
          }
        })}
        rowCount={journeyPlaces.length}
        paginated={false}
      />

      <Modal
        isOpen={modalState.create}
        title={"Ajouter l'adresse d'un collège/résidence"}
        icon={LocationOneIcon}
        size="medium"
        hideActions
        onCancel={() => closeModel('create')}
      >
        <OlJourneyPlaceForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{
            isSameAddresForTuition: true
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create'),
            onConfirm: (data) => {
              form.setValue('journeyPlaces', [...journeyPlaces, data])
              toggleModal('create')
            }
          })}
        />
      </Modal>

      {selectedData && (
        <>
          <Modal
            isOpen={modalState.edit}
            title={'Modifier une adresse de séjour'}
            size="medium"
            icon={LocationOneIcon}
            hideActions
            onCancel={() => closeModel('edit')}
          >
            <OlJourneyPlaceForm
              className={MODAL_BODY_CLASSNAME}
              defaultValues={selectedData}
              buttonsConfig={modalButtonConfig({
                onCancel: () => toggleModal('edit'),
                onConfirm: (data) => {
                  form.setValue(
                    'journeyPlaces',
                    journeyPlaces.map((journeyPlace) => {
                      if (journeyPlace.id === selectedData.id) {
                        return {
                          ...journeyPlace,
                          ...data
                        }
                      }
                      return journeyPlace
                    })
                  )
                  toggleModal('edit')
                }
              })}
            />
          </Modal>

          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: () => {
                form.setValue(
                  'journeyPlaces',
                  journeyPlaces.filter((journeyPlace) => journeyPlace.id !== selectedData.id)
                )
                toggleModal('delete')
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={`Supprimer l'adresse de séjour "${selectedData.name}"`}
            icon={DeleteIcon}
            variant={'destructive'}
          />
        </>
      )}
    </ActionTableWrapper>
  )
}

export default OlJourneyPlaceOlForm
